import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';

import { useState, type JSX } from 'react';

type ConfirmDialogResult = 'confirm' | 'cancel';

type UIProps = {
    open: boolean;
    onClose: (result: ConfirmDialogResult) => void;
    title: string;
    message: JSX.Element | JSX.Element[];
};

function UI({ open, onClose, title, message }: UIProps) {
    return (
        <Dialog open={open} onClose={() => onClose('cancel')}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {message}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose('cancel')} variant='outlined' autoFocus={true}>
                    いいえ
                </Button>
                <Button onClick={() => onClose('confirm')} variant='contained'>
                    はい
                </Button>
            </DialogActions>
        </Dialog>
    );
}

type ConfirmDialogMessage = string | string[] | JSX.Element;

type ConfirmDialogHook = {
    openConfirmDialog: () => Promise<ConfirmDialogResult>;
    setConfirmDialogMessage: (message: ConfirmDialogMessage, overwrite?: boolean) => void;
};

type ConfirmDialogProps = {
    title: string;
    message?: ConfirmDialogMessage;
};

type UseConfirmDialog = [(props: ConfirmDialogProps) => JSX.Element, ConfirmDialogHook];

export function useConfirmDialog(): UseConfirmDialog {
    const [resolve, setResolve] = useState<(value: ConfirmDialogResult) => void>();
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState<JSX.Element | JSX.Element[]>();

    function onClose(result: ConfirmDialogResult) {
        setOpen(false);
        if (resolve) {
            resolve(result);
        }
    };

    function openConfirmDialog() {
        setOpen(true);
        return new Promise<ConfirmDialogResult>((resolve) => {
            setResolve(() => resolve);
        });
    };

    function setConfirmDialogMessage(args: ConfirmDialogMessage, overwrite?: boolean) {
        if (!overwrite && message) {
            return;
        }

        let ele: JSX.Element | JSX.Element[];

        if (typeof args === 'string' || args instanceof String) {
            ele = (<DialogContentText>{args}</DialogContentText>)
        } else if (Array.isArray(args)) {
            const messages = args;
            ele = messages.map(msg => (<DialogContentText>{msg || <br />}</DialogContentText>));
        } else {
            ele = args;
        }

        setMessage(ele);
    }

    function ConfirmDialog(props: ConfirmDialogProps): JSX.Element {
        if (props.message) {
            setConfirmDialogMessage(props.message, true);
        }

        return (<UI open={open} onClose={onClose} title={props.title} message={message!} />);
    }

    return [ConfirmDialog, { openConfirmDialog, setConfirmDialogMessage }];
};
